import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import Layout from 'components/common/layout/Layout';
import SearchFilter from 'components/common/forms/SearchFilter';
import DualHeaderTable from 'components/common/lists/DualHeaderTable';
import Paging from 'components/common/nav/Paging';
import TableForm from 'components/common/lists/TableForm';
import SecondDualHeaderTable from 'components/common/lists/SecondDualHeaderTable';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import styles from 'styles/pages/professor/ClinicalPractice.module.scss';
import { DEFAULT_PAGE_SIZE } from 'constants/page';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TTableColumn } from 'types/components/table';
import {
  TEvalautionChartForGradeParams,
  TEvaluationChartCountInfo,
  TEvaluationChartCountParams,
  TEvaluationChartForGradeInfo,
} from 'types/apis/professor';
import {
  getEvaluationChartCount,
  getEvaluationChartListForGrade,
} from 'apis/professor';
import { getChartRoundList } from 'apis/common';
import useModal from 'hooks/useModal';

const tableColumns: TTableColumn[] = [
  {
    name: 'no',
    label: '번호',
    width: '70px',
  },
  { name: 'chartTypeName', label: '차트종류', width: '280px' },
  { name: 'studentLoginId', label: '학번', width: '110px' },
  { name: 'studentName', label: '이름', width: '96px' },
  { name: 'score', label: '평가결과', width: '120px' },
  { name: 'lastSubmitAt', label: '최종제출일시', width: '200px' },
  { name: 'lastEvaluatedAt', label: '평가일시', width: '144px' },
];

const ClinicalPractice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const { openModal } = useModal();

  const [page, setPage] = useState(Number(query.get('page')) || 1);
  const [params, setParams] = useState({
    page,
    size: DEFAULT_PAGE_SIZE,
    courseInfoId: Number(query.get('courseInfoId')),
    chartRound: Number(query.get('chartRound')),
    chartType: Number(query.get('chartType')) || undefined,
    score: query.get('score') || undefined,
    searchWord: query.get('searchWord') || undefined,
    isNeedEvaluation: query.get('isNeedEvaluation') === 'true' || undefined,
  });
  const [isLoading, setIsLoading] = useState(false);

  const { data: ChartRoundData } = useQuery(
    'getChartRoundList',
    () => getChartRoundList(params.courseInfoId.toString()),
    {
      enabled: !!params.courseInfoId,
    }
  );

  const evaluationChartTitle = ChartRoundData?.data.find(
    (chart) => chart.chartRound === Number(params.chartRound)
  )?.registeredChartName;

  const paramsForCount = {
    courseInfoId: Number(query.get('courseInfoId')),
    chartRound: Number(query.get('chartRound')),
  };
  const { data: getEvaluationChartCountData } = useQuery(
    ['getEvaluationChartCount', paramsForCount],
    () =>
      getEvaluationChartCount(paramsForCount as TEvaluationChartCountParams),
    { enabled: !!paramsForCount.courseInfoId && !!paramsForCount.chartRound }
  );

  const studentCount =
    getEvaluationChartCountData?.data?.totalStudentCount || 0;

  const finishCount = getEvaluationChartCountData?.data
    ?.chartEvaluatedCountList as TEvaluationChartCountInfo[];

  const { data: getEvaluationChartListData, isFetching } = useQuery(
    ['getEvaluationChartListForGrade', params],
    () =>
      getEvaluationChartListForGrade(params as TEvalautionChartForGradeParams),
    {
      enabled: !!params.courseInfoId && !!params.chartRound,
    }
  );

  const tableData = getEvaluationChartListData?.data?.evaluateChartList || [];
  const tablePaging = getEvaluationChartListData?.data?.paging;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickChartDetail = (row: TEvaluationChartForGradeInfo) => {
    const chartRoute = ChartInfo.find(
      (chart) => chart.name === row.chartTypeName
    )?.route;
    const courseId = query.get('courseId');
    if (
      !chartRoute ||
      !row.evaluationChartId ||
      !courseId ||
      !row.studentLoginId
    ) {
      openModal({
        title: '차트 조회 실패',
        text: '관리자에게 문의해 주세요.',
      });
      return;
    }
    sessionStorage.setItem('route', 'assessment');
    sessionStorage.setItem('page', String(page));
    navigate(
      `/feedback${chartRoute}/${
        row.evaluationChartId
      }?courseId=${courseId}&courseInfoId=${params.courseInfoId}&chartRound=${
        params.chartRound
      }&studentUserId=${row.studentUserId}&tab=${
        row.evaluationRound === 2 ? 3 : 1
      }`,
      {
        state: {
          from: {
            pathname: location.pathname,
            search: location.search,
          },
        },
      }
    );
  };

  useEffect(() => {
    setParams({
      ...params,
      page,
      courseInfoId: Number(query.get('courseInfoId')),
      chartRound: Number(query.get('chartRound')),
      chartType: Number(query.get('chartType')) || undefined,
      score: query.get('score') || undefined,
      searchWord: query.get('searchWord') || undefined,
      isNeedEvaluation: query.get('isNeedEvaluation') === 'true' || undefined,
    });
  }, [location.search, page]);

  return (
    <Layout>
      <h1 className="main_title mt40 mb24">실습평가</h1>

      <SearchFilter
        dropdown={[
          {
            id: 'course',
            placeholder: '강의명',
            width: '190px',
          },
          {
            id: 'courseInfo',
            placeholder: '분반',
            width: '190px',
          },
          {
            id: 'chartRound',
            placeholder: '(회차) 회차명',
            width: '196px',
          },
          {
            id: 'chartType',
            placeholder: '차트 종류',
            width: '190px',
            useAll: true,
          },
          {
            id: 'score',
            placeholder: '평가 결과',
            width: '190px',
            useAll: true,
          },
        ]}
        input={{
          placeholder: '이름, 학번을 입력해 주세요.',
          width: '300px',
          position: 'bottom',
        }}
        checkbox={[
          {
            id: 'isNeedEvaluation',
            label: '평가 필요한 학생만 보기',
          },
        ]}
        childrenPosition="middle"
        onChange={() => setPage(1)}
      >
        <div className={styles.table_wrapper}>
          <div className="flex justify_between items_center mb24">
            <h2 className={styles.title}>
              현재 진행 상태 : {evaluationChartTitle}
            </h2>
            <span className="fw700">
              정원 : <span>{studentCount}</span>(명)
            </span>
          </div>
          <Swiper
            className={styles.dual_table_swiper}
            pagination={true}
            modules={[Pagination]}
            style={{ height: '254px' }}
          >
            <SwiperSlide>
              <DualHeaderTable
                studentCount={studentCount}
                finishCount={finishCount}
              />
            </SwiperSlide>
            <SwiperSlide>
              <SecondDualHeaderTable
                studentCount={studentCount}
                finishCount={finishCount}
              />
            </SwiperSlide>
          </Swiper>
        </div>
      </SearchFilter>

      <TableForm
        data={tableData}
        columns={tableColumns}
        paging={tablePaging}
        onClickRow={onClickChartDetail}
        idField="evaluationChartId"
        emptyMessage="제출된 차트가 없습니다."
        isLoading={isLoading}
      />

      {tablePaging && tablePaging.totalElements > 0 && (
        <div className="mt32">
          <Paging
            activePage={page}
            totalItemsCount={tablePaging.totalElements}
            handlePageChange={setPage}
          />
        </div>
      )}
    </Layout>
  );
};
export default ClinicalPractice;
